// @flow
import { useContext, useMemo } from 'react';
import { Environment } from '@ta/lithium.context';

export const today = new Date();

/**
 * @return true if the given date is the first day of the month it's in
 */
export const isFirstOfMonth = (date: Date) => date.getDate() === 1;

/**
 * @return true if the given date is the last day of the month it's in
 */
export const isLastOfMonth = (date: Date) =>
  isFirstOfMonth(new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1));

/**
 * @return The first day of the month that the supplied date is in
 */
export const getFirstDayOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);

/**
 * @return the difference in days between the two dates
 */
export const getDifferenceDays = (date1: Date, date2: Date) =>
  Math.floor(Math.abs(date1.getTime() - date2.getTime()) / (1000 * 3600 * 24));

/**
 * @return the difference in months between the two dates
 */
export const getDifferenceMonths = (month1: Date, month2: Date) =>
  Math.abs(
    12 * (month2.getFullYear() - month1.getFullYear()) + (month2.getMonth() - month1.getMonth()),
  );

/**
 * @return The last day of the month that the supplied date is in
 */
export const getLastDayOfMonth = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth() + 1, 0);

/**
 * @return an array of localize month abbreviations (e.g. ['Jan', 'Feb', etc...])
 */
export const useShortMonthLabels = (): string[] => {
  const { locale } = useContext(Environment);

  return useMemo(() => {
    const dateFormat = new Intl.DateTimeFormat(locale.code, { month: 'short' });
    return Array(12)
      .fill(0)
      .map((ignored, monthIndex) => dateFormat.format(new Date(0, monthIndex, 1)));
  }, [locale.code]);
};

/**
 * A map of locale codes to the first day of the week (0 = Sunday, 1 = Monday), with Monday as the
 * default for all other locales. Ideally this wouldn't be hardcoded, but it's not currently
 * available from any API.
 */
const dayOfWeekOffsetByLocale = new Map([
  ['en-US', 0],
  ['en-CA', 0],
  ['en-HK', 0],
  ['he-IL', 0],
  ['ja-JP', 0],
  ['pt-BR', 0],
  ['vi-VN', 0],
  ['zh-Hans', 0],
  ['zh-Hans-US', 0],
  ['zh-Hant-HK', 0],
  ['zh-Hant-TW', 0],
]);

const defaultDayOfWeekOffset = 1;

/**
 * @return several date localization functions used by react-day-picker
 */
export const useLocaleUtils = () => {
  const { locale } = useContext(Environment);

  return useMemo(() => {
    const dayOfWeekOffset = dayOfWeekOffsetByLocale.has(locale.code)
      ? dayOfWeekOffsetByLocale.get(locale.code)
      : defaultDayOfWeekOffset;

    const dayFormat = new Intl.DateTimeFormat(locale.code, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

    const monthTitleFormat = new Intl.DateTimeFormat(locale.code, {
      month: 'long',
      year: 'numeric',
    });

    const weekdayShortFormat = new Intl.DateTimeFormat(locale.code, {
      weekday: 'short',
    });

    const weekdayLongFormat = new Intl.DateTimeFormat(locale.code, {
      weekday: 'long',
    });

    return {
      formatDay: (date: Date) => dayFormat.format(date),
      formatMonthTitle: (date: Date) => monthTitleFormat.format(date),
      formatWeekdayShort: (weekday: number) =>
        weekdayShortFormat.format(new Date(0, 0, weekday + dayOfWeekOffset)),
      formatWeekdayLong: (weekday: number) =>
        weekdayLongFormat.format(new Date(0, 0, weekday + dayOfWeekOffset)),
      getFirstDayOfWeek: () => dayOfWeekOffset,
    };
  }, [locale]);
};
